import React, { useContext, useMemo, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import Heading from '../../../generic-components/Heading';
import { useStore } from '../../../store';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { useMoveBucketMoney } from '../Hooks/useMoveBucketMoney';
import { FlowContext } from '../context';
import { STEPS, STYLES } from '../consts';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { USER_MESSAGE_TYPES } from '../../../flows/UserMessageFlow/constants';
import { POPUP_LIFESPAN } from '../../../flows/emergencyBucketDashboard/constants';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { ReactComponent as CloseIcon } from '../../../assets/svg/close.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/icons/svg/stamp-icons/ic-stamp-routes.svg';
import { useActivateSavingBucket } from '../Hooks/useActivateSavingBucket';
import { useUploadGenericFile } from '../../../hooks/utilities/useUploadGenericFile';
import { GENERIC_FILE_UPLOAD_TYPES } from '../../../types';

export const BucketMoneyMovement = () => {
  const context = useContext(FlowContext);
  const { balance: accountBalance } = useStore((state) => state.getPrimaryAccount());
  const summaryData = useStore((state) => state.getSummaryData());
  const { emergencyBucketResponse } = summaryData;
  const [amountToAdd, setAmountToAdd] = useState<number>(0);
  const [movementMode] = useState(context.moneyMovementMode);
  const { setMessageObject } = useStore((state) => state);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const { t } = useTranslation();
  const { mutateAsync: asyncActivateBucket } = useActivateSavingBucket();

  const { mutateAsync, isLoading } = useMoveBucketMoney({ direction: movementMode, amount: amountToAdd });

  const { refetch: refreshSummaryData, isRefetching } = useAccountSummaryQuery();

  const { mutateAsync: postUploadSnapShot, isLoading: isLoadingUploadFile } = useUploadGenericFile();

  const dynamicProps = useMemo(
    () => ({
      title: movementMode ? t('SAVINGS_BUCKET.MOVE_MONEY.WITHDRAW_AMOUNT') : t('SAVINGS_BUCKET.MOVE_MONEY.ADD_AMOUNT'),
      balanceDisclaimer: movementMode
        ? `in ${context.isSavingsBucket ? t('EMERGENCY_BUCKET_HEADER_PRO') : t('EMERGENCY_BUCKET_HEADER_BASIC')}`
        : t('SAVINGS_BUCKET.MOVE_MONEY.IN_CHECKING'),
      errorMessage: movementMode
        ? `${t('SAVINGS_BUCKET.MOVE_MONEY.ERROR_PRE')} ${context.isSavingsBucket ? t('SAVINGS_BUCKET.MOVE_MONEY.SAVINGS') : t('SAVINGS_BUCKET.MOVE_MONEY.EMERGENCY')} ${t('SAVINGS_BUCKET.MOVE_MONEY.ERROR_POST')}`
        : t('SAVINGS_BUCKET.MOVE_MONEY.ADD_BALANCE_ERROR'),
      cta: movementMode ? t('SAVINGS_BUCKET.MOVE_MONEY.WITHDRAW') : t('SAVINGS_BUCKET.MOVE_MONEY.ADD'),
      balanceValue: movementMode ? emergencyBucketResponse.balance : accountBalance,
    }),
    [movementMode],
  );

  const isLimitReached = useMemo(() => amountToAdd > dynamicProps.balanceValue, [amountToAdd, dynamicProps]);
  const handleAmountChange = (amount: string) => {
    setAmountToAdd(Number(amount));
  };

  const handleSubmit = async () => {
    await postUploadSnapShot({
      fileUsageType: GENERIC_FILE_UPLOAD_TYPES.SAVINGS_ACCOUNT,
      imageName: `${Date.now()}`,
    });
    try {
      if (!emergencyBucketResponse.active) {
        await asyncActivateBucket();
      }
      await mutateAsync();
      await refreshSummaryData();
      setMessageObject({
        type: USER_MESSAGE_TYPES.UPPER_MESSAGE,
        useLottie: true,
        iconSource: '',
        messageText: `$${amountToAdd} has been ${movementMode ? 'withdrawn' : 'deposited'} successfully ${movementMode ? 'from' : 'to'} your ${context.isSavingsBucket ? 'Savings Account' : 'Emergency Bucket'}`,
        autoCloseTimeout: POPUP_LIFESPAN,
        callback: () => {
          setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE });
        },
      });
      context.changeActiveStep(STEPS.DASHBOARD);
    } catch (e) {
      console.error(e);
      setIsErrorMessage(true);
    }
  };

  return (
    <div className={STYLES.MOVE_MONEY.wrapper}>
      <Heading title={dynamicProps.title} className={STYLES.MOVE_MONEY.mainTitle} />
      <div className='flex items-center gap-2'>
        <Heading title={convertValueToUSD(dynamicProps.balanceValue, 2)} className={STYLES.MOVE_MONEY.amountValue} />
        <Heading title='Available' className={STYLES.MOVE_MONEY.amountLeft} />
      </div>
      <Heading
        title={dynamicProps.balanceDisclaimer}
        className={`${STYLES.MOVE_MONEY.amountLeft} ${STYLES.MOVE_MONEY.amountLeftLastLine}`}
      />
      <CurrencyInput
        className={`${STYLES.MOVE_MONEY.currencyInput} ${isLimitReached ? 'border-red02 focus:border-red02' : ''}`}
        placeholder='$0.00'
        decimalsLimit={2}
        prefix='$'
        autoFocus
        onValueChange={(value) => handleAmountChange(value || '0')}
      />
      {isLimitReached && <Heading title={dynamicProps.errorMessage} className={STYLES.MOVE_MONEY.errorMessage} />}
      <LiliButton
        onClick={handleSubmit}
        isLoading={isLoading || isRefetching || isLoadingUploadFile}
        title={dynamicProps.cta}
        disabled={isLimitReached || !amountToAdd}
        className={STYLES.MOVE_MONEY.cta}
        id='elBtnAddAmount'
      />
      {isErrorMessage && (
        <UserMessage onClick={() => setIsErrorMessage(false)} id=''>
          <div className='w-[400px] rounded-[12px] bg-bw00 min-h-[504px] px-[12px] pt-[12px] pb-[40px] flex flex-col items-center fadeIn'>
            <CloseIcon onClick={() => setIsErrorMessage(false)} className='w-[40px] self-baseline mb-[40px]' />
            <ErrorIcon className='mb-[50px]' />
            <Heading title='Oops!' className='font-metricRMedium text-[18px] text-bw08 mb-[8px]' />
            <Heading
              type='HTML'
              title='Something went wrong. <br> Please try again later'
              className='font-metricR text-[16px] text-bw07 text-center'
            />
            <LiliButton
              onClick={() => setIsErrorMessage(false)}
              className='w-[170px] h-[54px] mt-auto'
              title='Ok'
              id='errorMessageAction'
            />
          </div>
        </UserMessage>
      )}
    </div>
  );
};
